import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";
import { scrollToId } from "./scroll";
import { trackEvent } from "./track";
import { useI18n } from "../../i18n";

const TERMS = [12, 24, 36, 48, 60];
const LOCALES = { fr: "fr-CA", en: "en-CA", es: "es" };

const computePayment = (principal, annualRate, months) => {
  if (annualRate === 0) return principal / months;
  const r = annualRate / 100 / 12;
  const f = Math.pow(1 + r, months);
  return (principal * r * f) / (f - 1);
};

export const Calculator = () => {
  const { t, lang } = useI18n();
  const [amount, setAmount] = useState("50000");
  const [term, setTerm] = useState("36");
  const [rate, setRate] = useState("10");
  const started = useRef(false);

  const onChange = (setter) => (e) => {
    if (!started.current) {
      started.current = true;
      trackEvent("calculator_started", {});
    }
    setter(e.target.value);
  };

  const a = parseFloat(amount);
  const m = parseInt(term, 10);
  const r = parseFloat(rate);
  const errors = {
    amount: amount !== "" && (!isFinite(a) || a <= 0 || a > 100000000) ? t("calc.errAmount") : null,
    term: term !== "" && (!isFinite(m) || m <= 0 || m > 600) ? t("calc.errTerm") : null,
    rate: rate !== "" && (!isFinite(r) || r < 0 || r > 100) ? t("calc.errRate") : null,
  };
  const valid =
    isFinite(a) && a > 0 && a <= 100000000 &&
    isFinite(m) && m > 0 && m <= 600 &&
    isFinite(r) && r >= 0 && r <= 100;
  const payment = valid ? computePayment(a, r, m) : null;

  const fmt = (n, digits = 2) =>
    new Intl.NumberFormat(LOCALES[lang] || "fr-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(n);

  const goContact = () => {
    trackEvent("calculator_cta_clicked", { language: lang });
    if (valid) {
      window.dispatchEvent(
        new CustomEvent("prefill-simulation", {
          detail: {
            financing_amount: a,
            term_months: m,
            estimated_rate: r,
            estimated_payment: Math.round(payment * 100) / 100,
            message: t("calc.contactMessage"),
          },
        })
      );
    }
    scrollToId("contact");
  };

  const field = (id, label, value, setter, err, extra = null, describedBy = null) => (
    <div>
      <label htmlFor={id} className="block font-heading text-sm tracking-[0.12em] uppercase text-[#8A9DB1] mb-3">
        {label}
      </label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={onChange(setter)}
        aria-invalid={!!err}
        aria-describedby={describedBy || undefined}
        className="cs-input w-full px-5 py-4 text-base"
        data-testid={`calc-${id}`}
      />
      {extra}
      {err && <p className="text-red-400 text-xs mt-2" role="alert">{err}</p>}
    </div>
  );

  return (
    <section id="calculateur" className="py-24 lg:py-36 border-t border-white/10" data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-14"
        >
          <p className="font-heading text-gold text-sm tracking-[0.25em] uppercase mb-6">{t("calc.eyebrow")}</p>
          <h2 className="font-heading font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl">{t("calc.title")}</h2>
          <p className="text-[#8A9DB1] text-base md:text-lg mt-5 max-w-xl">{t("calc.desc")}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8"
        >
          <div className="lg:col-span-7 service-card p-8 lg:p-10 space-y-7" style={{ transform: "none" }}>
            <p className="text-[#8A9DB1] text-xs uppercase tracking-[0.14em] font-heading">{t("calc.illustrative")}</p>
            {field("amount", t("calc.amountLabel"), amount, setAmount, errors.amount)}
            <div>
              {field("term", `${t("calc.termLabel")} (${t("calc.termUnit")})`, term, setTerm, errors.term)}
              <div className="flex flex-wrap gap-2 mt-3">
                {TERMS.map((mo) => (
                  <button
                    key={mo}
                    type="button"
                    onClick={() => { started.current = true; setTerm(String(mo)); }}
                    className={`px-4 py-1.5 text-sm font-heading border transition-colors duration-200 ${String(mo) === term ? "border-[#E8A33D] text-[#E8A33D]" : "border-white/20 text-[#8A9DB1] hover:border-white/50 hover:text-white"}`}
                    data-testid={`calc-term-${mo}`}
                  >
                    {mo}
                  </button>
                ))}
              </div>
            </div>
            {field("rate", `${t("calc.rateLabel")} (%)`, rate, setRate, errors.rate, null, "calc-rate-help")}
            <p id="calc-rate-help" className="flex items-start gap-2 text-[#8A9DB1] text-xs leading-relaxed">
              <Info size={14} className="text-gold mt-0.5 shrink-0" /> {t("calc.rateHelp")}
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-[#E8A33D]/40 bg-white/[0.03] p-8 lg:p-10 h-full flex flex-col" data-testid="calc-result-card">
              <p className="font-heading text-sm tracking-[0.18em] uppercase text-[#8A9DB1] mb-4">{t("calc.resultTitle")}</p>
              <p className="font-heading font-bold tracking-tighter text-4xl lg:text-5xl text-gold" aria-live="polite" data-testid="calc-payment">
                {payment !== null ? fmt(payment) : "—"}
                {payment !== null && <span className="text-lg text-[#8A9DB1] font-normal ml-2">{t("calc.perMonth")}</span>}
              </p>
              <div className="mt-8 space-y-3 text-sm">
                {[
                  [t("calc.sumAmount"), valid ? fmt(a, 0) : "—"],
                  [t("calc.sumTerm"), valid ? `${m} ${t("calc.termUnit")}` : "—"],
                  [t("calc.sumRate"), valid ? `${r} %` : "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-white/10 pb-3">
                    <span className="text-[#8A9DB1]">{k}</span>
                    <span className="text-white font-heading">{v}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#8A9DB1] text-xs leading-relaxed mt-6" data-testid="calc-disclaimer">
                {t("calc.disclaimer")}
              </p>
              <button
                type="button"
                onClick={goContact}
                className="btn-gold inline-flex items-center justify-center gap-2 px-7 py-4 text-base mt-8"
                data-testid="calc-cta"
              >
                {t("calc.cta")} <ArrowRight size={17} />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Calculator;
